import { FileImageIcon, FileText, FolderClockIcon, FolderIcon, InfoIcon } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn, formatDate } from '@folio/lib';
import { hasOtherCategory } from '@folio/utils/document-categories';
import { makeAiSizeValidator } from '../../../forms/file-input/ai-document-size';
import { DeleteDocumentDialog } from '../../../features/document-viewer/DeleteDocumentDialog';
import type {
  DocumentCustomCategorieMapping,
  DocumentViewerDocument,
} from '../../../features/document-viewer/types';
import { Button } from '../../../ui/button';
import { DOCUMENT_AUTHORIZED_FORMATS, FileDropzone } from '../../../ui/file-dropzone/file-dropzone';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../ui/tooltip';
import { DocumentRowActions } from '../DocumentRowActions';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text, Title } from '@folio/components/ui/text';

export type CollectionDocument = Pick<DocumentViewerDocument, 'type' | 'category'> & {
  id: string;
  name: string;
  signedUrl?: string;
  createdAt: string;
  metadata: Record<string, unknown>;
};

export type DocumentGroup<TDocument extends CollectionDocument> = {
  id: string;
  label: string;
  requirements?: string;
  required?: boolean;
  documents: readonly TDocument[];
};

export type DocumentsCollectionProps<TDocument extends CollectionDocument> = {
  groups: readonly DocumentGroup<TDocument>[];
  upload?: {
    onDrop: (files: File[]) => void | Promise<unknown>;
    isUploading?: boolean;
    authorizedFormats?: Record<string, string[]>;
    multiple?: boolean;
    minPages?: number;
    enforceAiSizeLimit?: boolean;
    helpText?: ReactNode;
  };
  classificationCategories?: DocumentCustomCategorieMapping[];
  getClassificationCategories?: (
    document: TDocument,
  ) => DocumentCustomCategorieMapping[] | undefined;
  getDocumentLabel?: (document: TDocument) => string;
  onView?: (document: TDocument) => void;
  onDelete?: (document: TDocument) => Promise<unknown> | unknown;
  renderBadges?: (document: TDocument) => ReactNode;
  renderBelowRow?: (document: TDocument) => ReactNode;
  afterUpload?: ReactNode;
  emptyCategories?: 'hidden' | 'required' | 'all';
  showGroupHeaders?: boolean;
};

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'heic']);

function FileFormatIcon({ name }: { name: string }) {
  const extension = name.split('.').pop()?.toLowerCase();
  return extension && IMAGE_EXTENSIONS.has(extension) ? (
    <FileImageIcon className="h-4 w-4 shrink-0 text-sky-600" />
  ) : (
    <FileText className="h-4 w-4 shrink-0 text-slate-500" />
  );
}

export function DocumentsCollection<TDocument extends CollectionDocument>({
  groups,
  upload,
  classificationCategories,
  getClassificationCategories,
  getDocumentLabel = document => document.name,
  onView,
  onDelete,
  renderBadges,
  renderBelowRow,
  afterUpload,
  emptyCategories = 'hidden',
  showGroupHeaders = true,
}: DocumentsCollectionProps<TDocument>) {
  const { t } = useTranslation();
  const aiSizeValidator = useMemo(
    () => (upload?.enforceAiSizeLimit ? makeAiSizeValidator(t) : undefined),
    [upload?.enforceAiSizeLimit, t],
  );
  const [documentToDelete, setDocumentToDelete] = useState<TDocument | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showOptionalCategories, setShowOptionalCategories] = useState(false);
  const visibleGroups = groups.filter(
    ({ documents, required }) =>
      documents.length > 0 ||
      emptyCategories === 'all' ||
      (emptyCategories === 'required' && (required || showOptionalCategories)),
  );

  const confirmDelete = async () => {
    if (!documentToDelete || !onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete(documentToDelete);
      setDocumentToDelete(null);
    } catch {
      // Failed deletions keep the confirmation open for retry.
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <TooltipProvider>
      <Stack>
        {upload && (
          <FileDropzone
            onDrop={async files => {
              await upload.onDrop(files);
            }}
            isPending={upload.isUploading}
            multiple={upload.multiple ?? true}
            minPages={upload.minPages}
            accept={upload.authorizedFormats ?? DOCUMENT_AUTHORIZED_FORMATS}
            helpText={upload.helpText}
            validator={aiSizeValidator}
          />
        )}
        {afterUpload}
        <Stack gap={2}>
          {visibleGroups.map(({ id, label, requirements, required, documents }) => (
            <section key={id} aria-label={label} className="border-b last:border-b-0">
              {showGroupHeaders && (
                <Group align="start" justify="between" gap={3} className="px-3 py-3">
                  <Group align="start" gap={2} className="min-w-0">
                    <FolderIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                    <Title order={3} size="sm" className="break-words">
                      {label}
                    </Title>
                    {requirements && requirements !== label && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={t('documentReview.requirements')}
                            className="h-5 w-5 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
                          >
                            <InfoIcon className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>{requirements}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </Group>
                  <span
                    className={cn(
                      'shrink-0 text-xs',
                      required && !documents.length
                        ? 'font-medium text-amber-800'
                        : 'text-muted-foreground',
                    )}
                  >
                    {required && !documents.length
                      ? t('documentReview.requiredMissing')
                      : t('fileInput.fileCount', { count: documents.length })}
                  </span>
                </Group>
              )}
              {documents.map(document => {
                const label = getDocumentLabel(document);
                const isViewable = Boolean(onView && document.signedUrl);
                const viewDescription = t(
                  isViewable ? 'fileInput.viewDocument' : 'fileInput.viewDocumentPreparing',
                );
                const dateTranslationKey =
                  document.type === 'generated'
                    ? 'documentsListsPolicy.generatedAt'
                    : document.type === 'supporting_document' || document.type === 'non_delegated'
                      ? 'documentsListsPolicy.uploadedAt'
                      : 'documentsListsPolicy.createdAt';
                const documentClassificationCategories =
                  getClassificationCategories?.(document) ?? classificationCategories;
                return (
                  <div
                    key={document.id}
                    className="relative ml-6 border-l border-slate-300 before:absolute before:left-0 before:top-6 before:h-px before:w-3 before:bg-slate-300"
                  >
                    <Group
                      wrap
                      justify="between"
                      gap={2}
                      className="relative py-2.5 pl-5 pr-3 text-sm transition-colors hover:bg-slate-50"
                    >
                      {onView && (
                        <Button
                          type="button"
                          variant="ghost"
                          aria-label={label}
                          aria-description={viewDescription}
                          title={viewDescription}
                          disabled={!isViewable}
                          onClick={() => onView(document)}
                          className="absolute inset-0 h-full w-full rounded-sm p-0 hover:bg-transparent focus-visible:ring-inset focus-visible:ring-offset-0 disabled:pointer-events-auto disabled:cursor-not-allowed disabled:opacity-100"
                        />
                      )}
                      <Group
                        gap={2.5}
                        className="pointer-events-none relative min-w-0 flex-[1_1_12rem]"
                      >
                        <FileFormatIcon name={document.name} />
                        <Stack gap={1} className="min-w-0 flex-1">
                          <Text
                            weight="normal"
                            tone={onView && !isViewable ? 'muted' : undefined}
                            className="break-words"
                          >
                            {label}
                          </Text>
                          <Text size="xs" tone="muted">
                            {t(dateTranslationKey, {
                              date: formatDate(new Date(document.createdAt)),
                            })}
                          </Text>
                        </Stack>
                      </Group>
                      <Group wrap gap={0} className="relative ml-auto gap-1.5">
                        {document.metadata.archived === 'true' && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                tabIndex={0}
                                aria-label={t('documentsListsPolicy.archivedTooltip')}
                              >
                                <FolderClockIcon className="h-4 w-4 text-muted-foreground" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t('documentsListsPolicy.archivedTooltip')}
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {renderBadges?.(document)}
                        <DocumentRowActions
                          document={document}
                          categories={
                            hasOtherCategory(
                              documentClassificationCategories,
                              document.category ?? undefined,
                            )
                              ? documentClassificationCategories
                              : undefined
                          }
                          onDelete={onDelete ? () => setDocumentToDelete(document) : undefined}
                        />
                      </Group>
                    </Group>
                    {renderBelowRow?.(document)}
                  </div>
                );
              })}
            </section>
          ))}
          {emptyCategories === 'required' &&
            groups.some(({ required, documents }) => !required && !documents.length) && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="self-start"
                aria-expanded={showOptionalCategories}
                onClick={() => setShowOptionalCategories(show => !show)}
              >
                {t(
                  showOptionalCategories
                    ? 'documentReview.hideOptional'
                    : 'documentReview.showOptional',
                )}
              </Button>
            )}
        </Stack>
      </Stack>
      {documentToDelete && (
        <DeleteDocumentDialog
          open
          onOpenChange={open => {
            if (!open && !isDeleting) setDocumentToDelete(null);
          }}
          onConfirm={confirmDelete}
          documentName={documentToDelete.name}
          isDeleting={isDeleting}
        />
      )}
    </TooltipProvider>
  );
}
